// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FilterLocationsModal from "@stoneage/App/Components/Locations/FilterLocationsModal";
import LocationResultsList from "@stoneage/App/Components/Locations/LocationResultsList";
import xUpdateSearchTextField from "@stoneage/App/Handlers/Pages/LocationFinder/UpdateSearchTextField";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface XLocationModel extends LocationModel {
    properties?: {
        locationType?: string;
        email?: string;
    };
}

interface OwnProps {
    extendedStyles?: LocationSearchFormStyles;
    onLocationSelected: (location: XLocationModel) => void;
    radiusSearchFilter?: number;
    geoLocationSearchText: string;
    isLoading: boolean;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    locations: XLocationModel[];
    categories: string;
    handleFilterModalApplyAction: (filters: { category?: string; country?: CountryModel; state?: StateModel }) => void;
    category: string;
    state: string;
    country: string;
}

// XNG-Changes
const mapStateToProps = (state: CustomAppState) => ({
    doesSalesRepExist: state.xLocationFinder.doesSalesRepExist,
});

const mapDispatchToProps = {
    xUpdateSearchTextField,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface LocationSearchFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    form?: InjectableCss;
    searchContainer?: InjectableCss;
    searchResultGridItem?: GridItemProps;
    // XNG-Changes
    modal?: ModalPresentationProps;
}

export const locationSearchFormStyles: LocationSearchFormStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchContainer: {
        css: css`
            display: grid;
            height: 642px;
        `,
    },
    searchResultGridItem: {
        width: 12,
    },
};

const StyledForm = getStyledWrapper("form");

const LocationSearchForm: React.FC<Props> = ({
    extendedStyles,
    onLocationSelected,
    radiusSearchFilter,
    geoLocationSearchText,
    isLoading,
    distanceUnitOfMeasure,
    locations,
    categories,
    xUpdateSearchTextField,
    handleFilterModalApplyAction,
    // XNG-Changes:
    category,
    state,
    country,
    doesSalesRepExist,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationSearchFormStyles, extendedStyles));
    const [searchRadiusFilter, setSearchRadiusFilter] = React.useState(radiusSearchFilter);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);
    const [showFilterModal, setShowFilterModal] = React.useState(false);

    React.useEffect(() => {
        setSearchRadiusFilter(radiusSearchFilter);
    }, [radiusSearchFilter]);

    React.useEffect(() => {
        setSearchGeoLocationFilter(geoLocationSearchText);
    }, [geoLocationSearchText]);

    const handleLocationClick = React.useCallback(
        (event: React.MouseEvent<Element, MouseEvent>, location: XLocationModel) => {
            event.preventDefault();
            onLocationSelected(location);
            xUpdateSearchTextField({
                locationSearchText: searchGeoLocationFilter,
                searchRadius: searchRadiusFilter,
            });
        },
        [onLocationSelected, searchGeoLocationFilter, searchRadiusFilter, xUpdateSearchTextField],
    );

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    };

    // XNG-Changes: Change Structure
    return (
        <>
            <StyledForm {...styles.form} onSubmit={handleFormSubmit} noValidate>
                <StyledWrapper
                    {...styles.searchContainer}
                    {...{ doesSalesRepExist }}
                    className="locationFinder-SearchContainer"
                >
                    {isLoading && (
                        <GridItem {...styles.searchResultGridItem}>
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {!isLoading && locations.length > 0 && (
                        <LocationResultsList
                            locations={locations}
                            distanceUnitOfMeasure={distanceUnitOfMeasure}
                            country={country}
                            state={state}
                            category={category}
                            onOpenFilterModal={() => setShowFilterModal(true)}
                            onLocationClick={handleLocationClick}
                        />
                    )}
                </StyledWrapper>
            </StyledForm>
            <Modal
                headline={translate("Filter Locations")}
                isOpen={showFilterModal}
                handleClose={() => setShowFilterModal(false)}
                {...styles.modal}
            >
                <FilterLocationsModal
                    locations={categories?.split("|")}
                    cancelAction={() => setShowFilterModal(false)}
                    applyFilterAction={handleFilterModalApplyAction}
                    category={category}
                    country={country}
                    state={state}
                />
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(LocationSearchForm));
